import React from "react";
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormResetField,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import {
  MAPPING_TYPE_MISMATCH_ERROR,
  PDF_MAPPING_TYPE,
} from "common/constants";
import {
  isCompatibleOption,
  PDFMappingOption,
} from "common/utils/documentTemplates";
import { Icon } from "../../../Common/Icons/LucideIcons";
import { Label } from "../../../Inputs";
import { Select } from "../../../Inputs/react-hook-form";
import {
  FieldRow,
  IconWrapper,
  ValueOptionWrapper,
} from "./__styles__/DocumentTemplates";
import { CreateDocumentTemplateForm } from "./CreateOrUpdateForm";

interface MappingFieldRowProps {
  index: number;
  field: CreateDocumentTemplateForm["mapping"][number];
  mappingOptions: PDFMappingOption[];
  control: Control<CreateDocumentTemplateForm>;
  getValues: UseFormGetValues<CreateDocumentTemplateForm>;
  setValue: UseFormSetValue<CreateDocumentTemplateForm>;
  resetField: UseFormResetField<CreateDocumentTemplateForm>;
  errors: FieldErrors<CreateDocumentTemplateForm>;
  loading: boolean;
}

const MappingFieldRow = ({
  index,
  field,
  mappingOptions,
  control,
  getValues,
  setValue,
  resetField,
  errors,
  loading,
}: MappingFieldRowProps) => {
  const currentMappingValue = useWatch({
    control,
    name: `mapping.${index}.value`,
  });
  const currentMapping = getValues(`mapping.${index}`);
  const selectedOption = mappingOptions.find(
    option => option.value === currentMappingValue,
  );

  const showBooleanTransformSelect =
    selectedOption?.transformOptions &&
    selectedOption.transformOptions.length &&
    field.pdfFieldType === PDF_MAPPING_TYPE.BOOLEAN &&
    selectedOption.type === PDF_MAPPING_TYPE.STRING;

  const booleanTransformError =
    (
      errors.mapping?.[index] &&
      "booleanTransform" in errors.mapping[index] &&
      Array.isArray(errors.mapping[index].booleanTransform)
    ) ?
      errors.mapping[index].booleanTransform?.[0]?.message
    : undefined;

  return (
    <React.Fragment>
      <FieldRow>
        <td>
          <Label
            text={field.label}
            required={false}
            style={{ fontWeight: "normal" }}
          />
        </td>
        <td>
          <Select
            name={`mapping.${index}.value`}
            size="medium"
            control={control}
            rules={{
              required: "This field is required",
              validate: value => {
                if (!loading) {
                  return (
                      isCompatibleOption({
                        option: mappingOptions.find(
                          option => option.value === value,
                        )!,
                        pdfFieldType: getValues(`mapping.${index}`)
                          .pdfFieldType,
                      })
                    ) ?
                      undefined
                    : `${MAPPING_TYPE_MISMATCH_ERROR}. PDF field type '${
                        getValues(`mapping.${index}`).pdfFieldType
                      }' is not compatible with the selected option.`;
                }
                return;
              },
            }}
            required
            options={mappingOptions.filter(option => {
              return isCompatibleOption({
                option,
                pdfFieldType: currentMapping.pdfFieldType,
              });
            })}
            error={errors.mapping?.[index]?.value?.message}
            onChange={value => {
              const option = mappingOptions.find(
                option => option.value === value,
              );
              if (option) {
                setValue(`mapping.${index}.forerunnerFieldType`, option.type);
                resetField(`mapping.${index}.booleanTransform`);
              }
            }}
          />
        </td>
      </FieldRow>
      {showBooleanTransformSelect && (
        <tr>
          <td> </td>
          <ValueOptionWrapper>
            <IconWrapper>
              <Icon
                iconName="corner-down-right"
                color="contentSecondary"
                size={16}
              />
            </IconWrapper>
            <Select
              name={`mapping.${index}.booleanTransform.0`}
              size="medium"
              control={control}
              rules={{
                required: "This field is required",
              }}
              required
              options={
                selectedOption.transformOptions?.map(opt => ({
                  label: opt.label,
                  value: opt.value,
                })) ?? []
              }
              error={booleanTransformError}
            />
          </ValueOptionWrapper>
        </tr>
      )}
    </React.Fragment>
  );
};

export default MappingFieldRow;
